/**
 * TanStack Query hooks for bark (distribution tracking) management.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { barksApi, platformConnectionsApi } from '../api';
import type {
  CreateBarkRequest,
  ScheduleBarkRequest,
  AddBarkItemRequest,
  MarkSentRequest,
  MarkFailedRequest,
  MarkSkippedRequest,
  CreatePlatformConnectionRequest,
  QueueParams,
} from '../types';

// ============================================================================
// Query Keys
// ============================================================================

export const barkKeys = {
  all: ['barks'] as const,
  site: (siteSlug?: string) => siteSlug ? [...barkKeys.all, 'site', siteSlug] as const : barkKeys.all,
  forContent: (contentType: string, contentId: string, siteSlug?: string) =>
    [...barkKeys.site(siteSlug), 'content', contentType, contentId] as const,
  detail: (barkId: string, siteSlug?: string) => [...barkKeys.site(siteSlug), 'detail', barkId] as const,
  summary: (barkId: string, siteSlug?: string) => [...barkKeys.site(siteSlug), 'summary', barkId] as const,
  queue: (siteSlug: string, params?: QueueParams) =>
    [...barkKeys.site(siteSlug), 'queue', params] as const,
  platformConnections: ['platform-connections'] as const,
  platformConnectionsForSite: (siteSlug?: string) => siteSlug
    ? [...barkKeys.platformConnections, siteSlug] as const : barkKeys.platformConnections,
  availablePlatforms: ['available-platforms'] as const,
};

// ============================================================================
// Barks Hooks
// ============================================================================

/** Fetch barks for a specific content item */
export function useBarksForContent(contentType: string, contentId: string, siteSlug?: string) {
  return useQuery({
    queryKey: barkKeys.forContent(contentType, contentId, siteSlug),
    queryFn: () => barksApi.listForContent(contentType, contentId, siteSlug),
    enabled: !!contentType && !!contentId,
  });
}

/** Fetch bark queue for a site */
export function useBarkQueue(siteSlug: string, params?: QueueParams) {
  return useQuery({
    queryKey: barkKeys.queue(siteSlug, params),
    queryFn: () => barksApi.listQueue(siteSlug, params),
    enabled: !!siteSlug,
  });
}

/** Fetch a single bark with all its items */
export function useBark(barkId: string, siteSlug?: string) {
  return useQuery({
    queryKey: barkKeys.detail(barkId, siteSlug),
    queryFn: () => barksApi.get(barkId, siteSlug),
    enabled: !!barkId,
  });
}

/** Fetch bark summary stats */
export function useBarkSummary(barkId: string, siteSlug?: string) {
  return useQuery({
    queryKey: barkKeys.summary(barkId, siteSlug),
    queryFn: () => barksApi.getSummary(barkId, siteSlug),
    enabled: !!barkId,
  });
}

/** Create a new bark */
export function useCreateBark(siteSlug?: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateBarkRequest) => barksApi.create(data, siteSlug),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: barkKeys.site(siteSlug) });
    },
  });
}

/** Delete a bark */
export function useDeleteBark(siteSlug?: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (barkId: string) => barksApi.delete(barkId, siteSlug),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: barkKeys.site(siteSlug) });
    },
  });
}

/** Update bark name */
export function useUpdateBarkName(siteSlug?: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ barkId, name }: { barkId: string; name: string | null }) =>
      barksApi.updateName(barkId, name, siteSlug),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: barkKeys.site(siteSlug) });
    },
  });
}

// ============================================================================
// Bark Items Hooks
// ============================================================================

/** Add an item to a bark */
export function useAddBarkItem(siteSlug?: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ barkId, data }: { barkId: string; data: AddBarkItemRequest }) =>
      barksApi.addItem(barkId, data, siteSlug),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: barkKeys.site(siteSlug) });
    },
  });
}

/** Mark item as sent */
export function useMarkItemSent(siteSlug?: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (variables: { itemId: string; barkId: string; data: MarkSentRequest }) =>
      barksApi.markItemSent(variables.itemId, variables.data, siteSlug),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: barkKeys.site(siteSlug) });
    },
  });
}

/** Mark item as failed */
export function useMarkItemFailed(siteSlug?: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (variables: { itemId: string; barkId: string; data: MarkFailedRequest }) =>
      barksApi.markItemFailed(variables.itemId, variables.data, siteSlug),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: barkKeys.site(siteSlug) });
    },
  });
}

/** Mark item as skipped */
export function useMarkItemSkipped(siteSlug?: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (variables: { itemId: string; barkId: string; data: MarkSkippedRequest }) =>
      barksApi.markItemSkipped(variables.itemId, variables.data, siteSlug),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: barkKeys.site(siteSlug) });
    },
  });
}

/** Reset item to pending */
export function useResetBarkItem(siteSlug?: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (variables: { itemId: string; barkId: string }) =>
      barksApi.resetItem(variables.itemId, siteSlug),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: barkKeys.site(siteSlug) });
    },
  });
}

/** Delete a single item */
export function useDeleteBarkItem(siteSlug?: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (variables: { itemId: string; barkId: string }) =>
      barksApi.deleteItem(variables.itemId, siteSlug),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: barkKeys.site(siteSlug) });
    },
  });
}

// ============================================================================
// Platform Connections Hooks
// ============================================================================

/** Fetch all platform connections */
export function usePlatformConnections(siteSlug?: string) {
  return useQuery({
    queryKey: barkKeys.platformConnectionsForSite(siteSlug),
    queryFn: () => platformConnectionsApi.list(siteSlug),
  });
}

/** Fetch available platforms */
export function useAvailablePlatforms() {
  return useQuery({
    queryKey: barkKeys.availablePlatforms,
    queryFn: () => platformConnectionsApi.listAvailable(),
  });
}

/** Create a new platform connection */
export function useCreatePlatformConnection(siteSlug?: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreatePlatformConnectionRequest) =>
      platformConnectionsApi.create(siteSlug ? { ...data, site_slug: siteSlug } : data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: barkKeys.platformConnectionsForSite(siteSlug) });
    },
  });
}

/** Delete a platform connection */
export function useDeletePlatformConnection() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => platformConnectionsApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: barkKeys.platformConnections });
    },
  });
}

/** Test a platform connection */
export function useTestPlatformConnection() {
  return useMutation({
    mutationFn: (id: number) => platformConnectionsApi.test(id),
  });
}

/** Atomically schedule all selected destinations; invalidate every site calendar interval. */
export function useScheduleBark(siteSlug?: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: ScheduleBarkRequest) => barksApi.schedule(data, siteSlug),
    onSuccess: () => { void queryClient.invalidateQueries({ queryKey: barkKeys.site(siteSlug) }); },
  });
}

export function useRescheduleBarkItem(siteSlug?: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ itemId, scheduledFor }: { itemId: string; scheduledFor: string | null }) =>
      barksApi.rescheduleItem(itemId, scheduledFor, siteSlug),
    onSuccess: () => { void queryClient.invalidateQueries({ queryKey: barkKeys.site(siteSlug) }); },
  });
}
